import { useTranslation } from 'react-i18next'
import type { SiteDocKind } from '@symphenia/shared-types'
import { useTerminology } from '@/lib/useTerminology'
import { erpArt } from '../arten/registry'
import '../arten'

interface KanonKopf {
  art: SiteDocKind
  nummer: string
  projekt: { nummer: string | null; bezeichnung: string }
  kunde: string | null
  baustelle: string | null
  datum: string
  titel: string
  fachteil: Record<string, unknown>
  fotos: { id: string; bildunterschrift: string | null }[]
  bestaetigungstext: string | null
  erfasser: { name: string }
  bezug: { art: 'SUPPLEMENT' | 'REPLACEMENT'; nummer: string; pruefwert: string } | null
}

/** Ohne Eintrag im Register: der Fachteil als schlichte Liste — lesbar, nie roh als JSON. */
export function FachteilListe({ fachteil }: { fachteil: Record<string, unknown> }) {
  const text = (w: unknown): string => (w === null || w === undefined ? '' : typeof w === 'object' ? Object.values(w as Record<string, unknown>).map(text).filter(Boolean).join(' · ') : String(w))
  return (
    <dl className="space-y-1 text-sm">
      {Object.entries(fachteil).map(([k, w]) =>
        Array.isArray(w) ? (
          <div key={k}>
            <dt className="text-muted-foreground">{k}</dt>
            <dd>
              <ul className="list-disc pl-5">
                {w.map((x, i) => (
                  <li key={i}>{text(x)}</li>
                ))}
              </ul>
            </dd>
          </div>
        ) : (
          <div key={k} className="flex gap-3">
            <dt className="w-32 shrink-0 text-muted-foreground">{k}</dt>
            <dd className="min-w-0 flex-1 break-words">{text(w)}</dd>
          </div>
        ),
      )}
    </dl>
  )
}

/**
 * AP-46 9. Schritt 2 — **Vorzeige im ERP**: der Stand, genau wie er eingefroren wird (aus
 * `GET /site-docs/:id/preview`, derselben Funktion wie Gerät und Server). Kopf, Fachteil der Art,
 * Bildunterschriften, Bestätigungstext.
 */
export function KanonAnsicht({ objekt }: { objekt: Record<string, unknown> }) {
  const { t } = useTranslation()
  const terms = useTerminology()
  const k = objekt as unknown as KanonKopf
  const art = erpArt(k.art)
  const zeile = (label: string, wert: string | null) =>
    wert ? (
      <div className="flex gap-3 text-sm">
        <dt className="w-32 shrink-0 text-muted-foreground">{label}</dt>
        <dd className="min-w-0 flex-1 break-words font-medium">{wert}</dd>
      </div>
    ) : null
  return (
    <article className="space-y-4 p-4">
      <header className="space-y-1 border-b pb-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-primary">
          {t(`siteDocs.kinds.${k.art}`)} · {k.nummer}
        </p>
        <h3 className="text-lg font-semibold">{k.titel}</h3>
        <dl className="space-y-1 pt-1">
          {zeile(terms.project, [k.projekt.nummer, k.projekt.bezeichnung].filter(Boolean).join(' '))}
          {zeile(t('siteDocs.preview.customer'), k.kunde)}
          {zeile(t('siteDocs.preview.site'), k.baustelle)}
          {zeile(t('siteDocs.preview.date'), k.datum.split('-').reverse().join('.'))}
          {zeile(t('siteDocs.preview.author'), k.erfasser.name)}
          {k.bezug && zeile(t(`siteDocs.preview.reference.${k.bezug.art}`), `${k.bezug.nummer} (${k.bezug.pruefwert.slice(0, 8)})`)}
        </dl>
      </header>
      {art ? <art.Vorzeige fachteil={k.fachteil} /> : <FachteilListe fachteil={k.fachteil} />}
      {k.fotos.length > 0 && (
        <p className="text-sm text-muted-foreground">
          {t('siteDocs.preview.photos', { count: k.fotos.length })}
          {k.fotos.some((f) => f.bildunterschrift) && `: ${k.fotos.map((f) => f.bildunterschrift).filter(Boolean).join(' · ')}`}
        </p>
      )}
      {k.bestaetigungstext && <p className="rounded-lg border-2 border-primary/40 bg-primary/5 p-3 font-medium">{k.bestaetigungstext}</p>}
    </article>
  )
}
